import { useEffect, useRef, useState } from 'react';
import AsteriskMark from './AsteriskMark';

// Welcome screen shown until the 3D room and fonts are ready.
// Stays up at least MIN_MS so the intro plays, never longer than MAX_MS.
const MIN_MS = 1400;
const MAX_MS = 6000;
const OUT_MS = 950;
const WORDS = ['Isak', 'Gerre'];

export default function Loader({ ready, onDone }: { ready: boolean; onDone: () => void }) {
  const [out, setOut] = useState(false);
  const countRef = useRef<HTMLSpanElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const readyRef = useRef(ready);
  const doneRef = useRef(onDone);
  readyRef.current = ready;
  doneRef.current = onDone;

  useEffect(() => {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const min = reduce ? 300 : MIN_MS;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = 'hidden';
    const t0 = performance.now();
    let shown = 0, raf = 0, timer = 0;

    const tick = (now: number) => {
      const t = now - t0;
      const done = readyRef.current || t > MAX_MS;
      // Ease toward 90% while waiting, then race to 100% once everything is in.
      const target = done ? 100 : 90 * (1 - Math.pow(1 - Math.min(1, t / min), 3));
      shown += (target - shown) * (done ? 0.2 : 0.12);
      if (done && target - shown < 0.5) shown = 100;
      if (countRef.current) countRef.current.textContent = String(Math.floor(shown)).padStart(3, '0');
      if (barRef.current) barRef.current.style.transform = `scaleX(${shown / 100})`;
      if (shown >= 100 && t >= min) {
        root.style.overflow = prevOverflow;
        setOut(true);
        timer = window.setTimeout(() => doneRef.current(), reduce ? 300 : OUT_MS);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); root.style.overflow = prevOverflow; };
  }, []);

  let n = 0;
  return (
    <div className={`loader${out ? ' is-out' : ''}`} role="status" aria-label="Loading Isak Gerre's portfolio">
      <div className="loader-cols" aria-hidden="true"><span /><span /><span /></div>
      <div className="loader-card" aria-hidden="true">
        <div className="loader-top">
          <span className="loader-mark"><AsteriskMark size={44} /></span>
          <div className="loader-shapes"><i className="s-circle" /><i className="s-square" /><i className="s-triangle" /></div>
        </div>
        <div className="loader-name">
          {WORDS.map(w => (
            <span key={w} className="loader-word">
              {[...w].map(ch => <span key={n} style={{ '--i': n++ } as React.CSSProperties}>{ch}</span>)}
            </span>
          ))}
        </div>
        <span className="eyebrow muted loader-sub">Front-end engineer · Malmö</span>
        <div className="loader-progress"><div ref={barRef} className="loader-bar" /></div>
        <div className="loader-foot"><span>Building the room</span><span><span ref={countRef}>000</span>%</span></div>
      </div>
    </div>
  );
}
